import { announceReconnected, whileVisible } from '../visibility'

/**
 * Whether the app can reach its own server, asked of the server rather than of
 * the browser.
 *
 * `navigator.onLine` was the only signal before this, and it answers a
 * different question — "does this device have a network interface up" — which
 * is true on hotel Wi-Fi behind a login page, on a phone with one bar that
 * passes nothing, and while this deployment's pod is restarting. Meanwhile the
 * service worker's `NetworkFirst` rule gives up on `/api/forecast` after 8 s
 * and hands back the saved bundle as a success, so the one symptom anybody
 * could see was a forecast that quietly stopped updating.
 *
 * - `live`: `/healthz` answered `{"status": "ok"}` recently.
 * - `offline`: the browser says there is no network at all. Nothing is asked;
 *   the answer is already known.
 * - `unreachable`: there is a network and our server is not answering on it —
 *   a timeout, a gateway error from the ingress, or something that is not our
 *   JSON, which is what a captive portal answers every URL with.
 *
 * Only `/healthz` decides `live`. A forecast "succeeding" is no evidence: the
 * service worker may have served it from its cache.
 */

export type ConnectionStatus = 'live' | 'offline' | 'unreachable'

export const connection = $state({
  status: (typeof navigator === 'undefined' || navigator.onLine
    ? 'live'
    : 'offline') as ConnectionStatus,
  /** When the server last answered. */
  lastContact: null as Date | null,
  /** When the current outage began; null while live. */
  since: null as Date | null,
  /** Bumps on every return to `live`, for the "Back online" notice. */
  restored: 0,
})

/** A healthy page asks this often. `/healthz` is a constant answer with no
 *  upstream behind it, so this is the cheapest request the server has. */
const LIVE_CHECK_MS = 30_000
/** A page that has lost the server asks this often, so the notice clears
 *  within seconds of it coming back rather than half a minute. */
const DOWN_CHECK_MS = 15_000
/** Longer than a healthy round trip on one bar of signal; short enough that a
 *  dead server is named before anybody wonders why nothing is loading. */
const TIMEOUT_MS = 6_000

let inFlight: Promise<void> | null = null

/** Ask the server now. Concurrent callers share one request. */
export function checkConnection(): Promise<void> {
  inFlight ??= probe().finally(() => (inFlight = null))
  return inFlight
}

async function probe(): Promise<void> {
  if (!navigator.onLine) {
    note('offline')
    return
  }
  const abort = new AbortController()
  const timer = setTimeout(() => abort.abort(), TIMEOUT_MS)
  try {
    // `no-store`: a health check answered from a cache is not a health check.
    // The service worker has no rule for /healthz, so this reaches the network.
    const resp = await fetch('/healthz', { cache: 'no-store', signal: abort.signal })
    const body = resp.ok ? await resp.json().catch(() => null) : null
    note(body?.status === 'ok' ? 'live' : 'unreachable')
  } catch {
    // The network can drop between the check above and the answer.
    note(navigator.onLine ? 'unreachable' : 'offline')
  } finally {
    clearTimeout(timer)
  }
}

function note(next: ConnectionStatus): void {
  if (next === 'live') connection.lastContact = new Date()
  const prev = connection.status
  if (next === prev) return
  connection.status = next
  if (next === 'live') {
    connection.since = null
    connection.restored++
    // Everything that refreshes on a timer was failing or serving saved copies
    // while the server was gone. Catch it all up now rather than one interval
    // from now — the forecast especially, whose on-screen age is the thing
    // the notice has just been telling somebody about.
    announceReconnected()
  } else if (prev === 'live') {
    connection.since = new Date()
  }
}

/**
 * Watch the connection for as long as the page is open. Returns a disposer.
 *
 * Checks once at start — a page booted from the service worker's precache on
 * a dead network would otherwise claim `live` until the first tick — then
 * every `LIVE_CHECK_MS` while live and every `DOWN_CHECK_MS` while not, only
 * while the page is visible, and at once when it becomes visible again.
 */
export function startConnectionWatch(): () => void {
  const tick = () => {
    const age = Date.now() - (connection.lastContact?.getTime() ?? 0)
    if (connection.status === 'live' && age < LIVE_CHECK_MS - 1000) return
    void checkConnection()
  }
  // The browser does know this much for certain, and knows it first.
  const onOffline = () => note('offline')

  void checkConnection()
  const stop = whileVisible(tick, DOWN_CHECK_MS)
  window.addEventListener('offline', onOffline)
  return () => {
    stop()
    window.removeEventListener('offline', onOffline)
  }
}

/** Tests only: module state outlives a component. */
export function _resetConnection(): void {
  inFlight = null
  connection.status = 'live'
  connection.lastContact = null
  connection.since = null
  connection.restored = 0
}
